import React, { useEffect, useMemo, useState } from 'react';
import useTheme from '@mui/material/styles/useTheme';

import { CXAvatar } from 'common/components/cx-avatar/cx-avatar';
import {
  Box,
  CircularProgress,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from 'common/components/material';
import { PersonLinkContainer } from 'common/components/person-link/person-link-container.component';
import { DrawerHeader } from 'common/components/material/Drawer';
import { ButtonTabs, CloseButton } from 'common/components/navigation';
import useDrawer from 'common/hooks/useDrawer';
import { WorkItemsForSkill } from 'features/matching-engine/components/MatchingEngineComponents/ProfileSkillBreakdown';
import {
  DrawerTitle,
  faintScrollbarStyles,
} from 'features/matching-engine/matching-engine.utils';
import { DrawerIdEnum } from 'features/org-root/enums/drawer-id.enum';
import SkillRatingReadView from 'features/org-root/components/SkillRatingReadView';
import { useGetProfile } from 'features/profiles';
import {
  EndorsedSkillWithEndorsers,
  Skill,
  useGetMySkillEndorsement,
  useGetSkillEndorsers,
} from 'features/skills';
import { SkillEndorsementSection } from './skill-endorsement-section';
import moment from 'moment';
import { useGetWorkItems } from 'features/matching-engine/hooks/useGetWorkItems';

export enum SkillEndorsementDrawerTabs {
  Activities = 'activities',
  Endorsements = 'endorsements',
}

const EndorsementsList: React.FC<{ endorsers: any[]; isLoading: boolean }> = ({
  endorsers,
  isLoading,
}) => {
  const { palette } = useTheme();

  if (isLoading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!endorsers.length) {
    return (
      <Box py={3} textAlign="center">
        <Typography variant="body2" fontWeight={600}>
          No endorsements yet
        </Typography>
        <Typography variant="caption" color={palette.Text.SubHeadline}>
          Be the first person to endorse this skill.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack
      divider={<Divider flexItem />}
      sx={{
        border: `1px solid ${palette.Divider}`,
        borderRadius: 2,
        backgroundColor: palette.Card.Bg,
        px: 0.75,
        py: 0.5,
      }}
    >
      {endorsers.map((endorser) => {
        const date = endorser.endorsementDate
          ? moment(endorser.endorsementDate).format('DD MMM YYYY')
          : undefined;

        return (
          <PersonLinkContainer
            key={endorser.id}
            person={endorser}
            subtitle={date ? `Updated ${date}` : 'Endorsed this skill'}
            showNavigationIndicator={false}
            trailingContent={
              endorser.rating ? <SkillRatingReadView rating={endorser.rating} /> : undefined
            }
          />
        );
      })}
    </Stack>
  );
};

export const SkillEndorsementDrawer: React.FC<{
  profileId: string;
  profileName?: string;
  skill: Skill;
  initialTab?: SkillEndorsementDrawerTabs;
  onClose?: () => void;
}> = ({ profileId, profileName, skill, initialTab = SkillEndorsementDrawerTabs.Activities, onClose }) => {
  const theme = useTheme();
  const { data: profile, isLoading: isProfileLoading } = useGetProfile({ id: profileId }, {
    enabled: !!profileId && !profileName,
  });
  const { closeDrawer } = useDrawer();
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const { data: workItems = [], isLoading: isActivitiesLoading } = useGetWorkItems({
    skillIds: [skill.id],
    profileId,
  });
  const {
    data: endorsers = [],
    isLoading: isEndorsersLoading,
  } = useGetSkillEndorsers(profileId, skill.id);
  const {
    data: myEndorsementContext,
    isLoading: isMyEndorsementLoading,
  } = useGetMySkillEndorsement(profileId, skill.id, {
    enabled: !!(profileId && skill.id),
  });
  const myEndorsement = myEndorsementContext?.endorsement;
  const activityCount = useMemo(
    () =>
      workItems.reduce(
        (total: number, workItem: { activityCount?: number }) =>
          total + Number(workItem.activityCount || 0),
        0,
      ),
    [workItems],
  );
  const tabs = useMemo(() => {
    if (isActivitiesLoading || isEndorsersLoading) return [];

    return [
      activityCount > 0
        ? {
            value: SkillEndorsementDrawerTabs.Activities,
            label: 'Activities',
            badgeLabel: String(activityCount),
          }
        : null,
      endorsers.length > 0
        ? {
            value: SkillEndorsementDrawerTabs.Endorsements,
            label: 'Endorsements',
            badgeLabel: String(endorsers.length),
          }
        : null,
    ].filter(Boolean) as Array<{
      value: SkillEndorsementDrawerTabs;
      label: string;
      badgeLabel: string;
    }>;
  }, [activityCount, endorsers.length, isActivitiesLoading, isEndorsersLoading]);
  const visibleSelectedTab = tabs.some(({ value }) => value === selectedTab)
    ? selectedTab
    : tabs[0]?.value;
  const currentEndorsement = useMemo<EndorsedSkillWithEndorsers>(
    () => ({
      skill,
      isEndorsedByLoggedInUser: !!myEndorsement,
      loggedInUserRating: myEndorsement?.rating,
      loggedInUserEndorsementDate: myEndorsement?.endorsementDate,
    }),
    [myEndorsement, skill],
  );

  useEffect(() => {
    if (visibleSelectedTab && visibleSelectedTab !== selectedTab) {
      setSelectedTab(visibleSelectedTab);
    }
  }, [selectedTab, visibleSelectedTab]);

  const closeCurrentDrawer = () => {
    closeDrawer(DrawerIdEnum.SKILL_ENDORSEMENT_DRAWER);
    onClose?.();
  };

  return (
    <>
      <DrawerHeader>
        <Stack spacing={0.75} px={2} pt={1.5} width="100%">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center">
              <CXAvatar src={profile?.photo} alt={profileName || profile?.fullName} />
              <DrawerTitle sx={{ fontSize: '1.25rem', fontWeight: 600 }} ml={1.5}>
                {profileName || profile?.fullName || (isProfileLoading ? <Skeleton width={180} /> : '')}
              </DrawerTitle>
            </Stack>
            <CloseButton clickHandler={closeCurrentDrawer} />
          </Stack>
          <Stack direction="row" alignItems="baseline" spacing={0.75}>
            <Typography
              variant="caption"
              color={theme.palette.Text.SubHeadline}
              flexShrink={0}
            >
              Skill
            </Typography>
            <Typography variant="body3" fontWeight={500}>
              {skill.name}
            </Typography>
          </Stack>
        </Stack>
      </DrawerHeader>

      <Box
        sx={{
          ...faintScrollbarStyles(theme),
          height: 'calc(100% - 96px)',
          overflowY: 'auto',
          px: 2,
          pb: 3,
          mt: 1.5,
        }}
      >
        {isMyEndorsementLoading ? (
          <Skeleton variant="rounded" height={148} sx={{ borderRadius: 2 }} />
        ) : (
          <SkillEndorsementSection
            profileId={profileId}
            profileFirstName={profile?.firstName || profileName?.split(' ')[0]}
            skill={skill}
            endorsement={currentEndorsement}
            isLoading={false}
          />
        )}

        {isActivitiesLoading || isEndorsersLoading ? (
          <Stack spacing={1.25} mt={2}>
            <Stack direction="row" spacing={1}>
              <Skeleton variant="rounded" width={132} height={38} />
              <Skeleton variant="rounded" width={152} height={38} />
            </Stack>
            <Skeleton variant="rounded" height={112} sx={{ borderRadius: 2 }} />
          </Stack>
        ) : tabs.length > 0 ? (
          <>
            <ButtonTabs
              selectedValues={visibleSelectedTab ? [visibleSelectedTab] : []}
              tabs={tabs}
              onChange={setSelectedTab}
              scroll
              sx={{ px: { xs: 1.5, md: 0 }, py: 1.5, gap: 0.75, mt: 1.25 }}
            />

            {visibleSelectedTab === SkillEndorsementDrawerTabs.Activities ? (
              <WorkItemsForSkill skillId={skill.id} profileId={profileId} />
            ) : visibleSelectedTab === SkillEndorsementDrawerTabs.Endorsements ? (
              <EndorsementsList endorsers={endorsers} isLoading={isEndorsersLoading} />
            ) : null}
          </>
        ) : null}
      </Box>
    </>
  );
};
